import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Divider } from 'ui-components/components/divider';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import FormattedCurrency from '../../../../../components/formatted-currency';

import { EACH_UOM, computeAbsoluteEaches } from '../../../../../constants/units-of-measure';
import { useEnableUnitsOfMeasure } from '../../../../../feature-flags/hooks/use-enable-units-of-measure';
import { messages } from './purchase-order-cost-summary-messages';
import styles from './purchase-order-cost-summary.styles.module.less';
import Global from '../../../../../store/global-selectors';
import { usePurchaseOrderContext } from '../../purchase-order-context';

type PurchaseOrderProduct = {
  quantity?: number;
  cost?: number;
  unitOfMeasure?: number | null;
  unitsOfMeasure?: { productUnitOfMeasureId: number; conversionFactor: number; parentProductUnitOfMeasureId?: number | null }[];
};

export const PurchaseOrderCostSummary = () => {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const { purchaseOrder } = usePurchaseOrderContext() as any;
  const intl = useIntl();
  const isUnitsOfMeasureEnabled = useEnableUnitsOfMeasure();
  const defaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const currencyCode = purchaseOrder.currencyCode || defaultCurrencyCode;

  const { products = [], shippingCost = 0, taxes = 0, tariffs = 0 } = purchaseOrder;

  const costOfGoods = useMemo(() => {
    return products.reduce((sum: number, p: PurchaseOrderProduct) => {
      const qty = p.quantity ?? 0;
      const cost = p.cost ?? 0;
      const rawUoms = p.unitsOfMeasure?.filter((u) => u.productUnitOfMeasureId !== EACH_UOM.productUnitOfMeasureId) ?? [];
      const absoluteEaches = isUnitsOfMeasureEnabled && (p.unitOfMeasure ?? 0) > 0 ? computeAbsoluteEaches(p.unitOfMeasure!, rawUoms) : 1;
      return sum + qty * cost * absoluteEaches;
    }, 0);
  }, [products, isUnitsOfMeasureEnabled]);

  const totalCost = costOfGoods + shippingCost + taxes + tariffs;

  return (
    <div className={styles['cost-summary-container']}>
      <Text className={styles['cost-summary-header']} textType={TextType.H4} value={intl.formatMessage(messages.costSummary)} />
      <FlexColumn className={styles['cost-summary-values']} gap={Gap.Space4}>
        <FlexRow className={styles['cost-summary-row']} gap={Gap.Space16}>
          <Text className={styles['cost-summary-label']} textType={TextType.Paragraph} value={intl.formatMessage(messages.costOfGoods)} />
          <Text
            className={styles['cost-summary-value']}
            textType={TextType.Paragraph}
            value={<FormattedCurrency currencyCode={currencyCode} value={costOfGoods} />}
          />
        </FlexRow>
        <FlexRow className={styles['cost-summary-row']} gap={Gap.Space16}>
          <Text className={styles['cost-summary-label']} textType={TextType.Paragraph} value={intl.formatMessage(messages.totalCost)} />
          <Text
            className={styles['cost-summary-value']}
            textType={TextType.Paragraph}
            value={<FormattedCurrency currencyCode={currencyCode} value={totalCost} />}
          />
        </FlexRow>
      </FlexColumn>
      <Divider className={styles.divider} orientation='horizontal' />
    </div>
  );
};
